import { useMemo, useRef, useEffect } from 'react';

/**
 * BlockAvailabilityDateStrip — 60-day horizontal scrollable date
 * picker. Flutter parity:
 * `block_availability_widget.dart:355-389` (the `DatePicker` widget
 * from the `date_picker_timeline` Flutter package — 60-card horizontal
 * strip starting today, 60px × 86px per card).
 *
 * Each card stacks [month abbreviation, day number, weekday]. The
 * selected card uses brand-primary background with white text. Cards
 * are click-only — keyboard navigation (left/right arrows) lands in
 * PR 3 when we wire the full focus model.
 */

interface Props {
  /** Currently-selected day. Should be start-of-day. */
  selectedDate: Date;
  /** Fires when the user taps a different card. */
  onDateChange: (date: Date) => void;
  /**
   * Number of days to render starting today. Default 60 (Flutter
   * parity). The parent panel's popover bounds MUST match this range
   * (today .. today + daysCount - 1) — see the alignment comment in
   * `block-availability-panel.tsx` (Samer review §2 on PR 2). A
   * selected day outside the strip's range produces no card
   * highlight and a silent `scrollIntoView` no-op — UX hole, not a
   * crash.
   */
  daysCount?: number;
  /**
   * When true, every card is non-interactive — `disabled` set on the
   * `<button>`, no hover state. Used by the panel's edit/view mode
   * (PR 5) so the user can see which day the existing block is on
   * without being able to change it.
   */
  disabled?: boolean;
}

interface DayCell {
  date: Date;
  iso: string;
  monthAbbr: string;
  day: number;
  weekdayAbbr: string;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

function isoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function BlockAvailabilityDateStrip({ selectedDate, onDateChange, daysCount = 60, disabled = false }: Props) {
  const cells = useMemo<DayCell[]>(() => {
    const today = startOfDay(new Date());
    const out: DayCell[] = [];
    for (let i = 0; i < daysCount; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push({
        date: d,
        iso: isoDay(d),
        monthAbbr: MONTH_ABBR[d.getMonth()],
        day: d.getDate(),
        weekdayAbbr: WEEKDAY_ABBR[d.getDay()],
      });
    }
    return out;
  }, [daysCount]);

  const selectedIso = isoDay(selectedDate);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll the selected card into view on mount + whenever the
  // parent changes selection from outside (e.g. opening edit mode in
  // PR 5 lands on the existing block's date).
  useEffect(() => {
    const sel = containerRef.current?.querySelector<HTMLButtonElement>(`[data-iso="${selectedIso}"]`);
    sel?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selectedIso]);

  return (
    <div
      ref={containerRef}
      className="flex gap-2 overflow-x-auto scrollbar-hide pb-1"
      role="listbox"
      aria-label="Select date"
    >
      {cells.map((cell) => {
        const isSelected = cell.iso === selectedIso;
        return (
          <button
            key={cell.iso}
            data-iso={cell.iso}
            role="option"
            aria-selected={isSelected}
            disabled={disabled}
            onClick={() => onDateChange(cell.date)}
            className={`flex-shrink-0 w-[64px] h-[88px] flex flex-col items-center justify-center rounded-card border transition-colors disabled:cursor-not-allowed ${
              isSelected
                ? 'bg-brand-primary text-white border-brand-primary'
                : `bg-[var(--surface-read)] text-text-primary border-[var(--surface-line)] ${disabled ? '' : 'hover:bg-grey-100'}`
            }`}
          >
            <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>
              {cell.monthAbbr}
            </span>
            <span className="text-lg font-bold leading-tight">{cell.day}</span>
            <span className={`text-xs ${isSelected ? 'text-white/80' : 'text-text-muted'}`}>
              {cell.weekdayAbbr}
            </span>
          </button>
        );
      })}
    </div>
  );
}
